import { Link } from 'react-router-dom';

export interface GridCard {
  id: string;
  title: string;
  /** Second line — the Arabic name, or a trim/price summary. */
  subtitle?: string;
  /** Short facts rendered under the title, e.g. "7 models · 3 live". */
  facts?: string[];
  status: 'draft' | 'live';
  href: string;
  imageUrl?: string;
  /** `contain` for logos on their own tile, `cover` for photography. */
  fit?: 'contain' | 'cover';
}

/**
 * Catalog view for brands and models.
 *
 * These records are overwhelmingly visual — a logo or a hero shot plus a name —
 * and a table row spends most of its width on nothing. Cards put the artwork
 * first and let a screen show a dozen at a glance.
 *
 * Reorder and delete stay hidden until hover or keyboard focus, so the card
 * reads as content rather than as a row of controls.
 */
export function CardGrid({
  items,
  onReorder,
  onDelete,
  canDelete,
  deleteLabel = 'Delete',
}: {
  items: GridCard[];
  onReorder: (ids: string[]) => void;
  onDelete?: (id: string) => void;
  canDelete: boolean;
  deleteLabel?: string;
}) {
  const move = (index: number, delta: number) => {
    const target = index + delta;
    if (target < 0 || target >= items.length) return;
    const ids = items.map((i) => i.id);
    const [moved] = ids.splice(index, 1);
    ids.splice(target, 0, moved!);
    onReorder(ids);
  };

  return (
    <ul className="card-grid">
      {items.map((item, index) => (
        <li key={item.id} className="grid-card">
          <Link to={item.href} className="grid-card-link">
            <span className={`grid-card-media ${item.fit ?? 'contain'}`}>
              {item.imageUrl ? (
                <img src={item.imageUrl} alt="" loading="lazy" />
              ) : (
                <span className="grid-card-placeholder" aria-hidden="true">
                  {item.title.slice(0, 2).toUpperCase()}
                </span>
              )}
            </span>

            <span className="grid-card-body">
              <span className="grid-card-head">
                <span className="grid-card-title">{item.title}</span>
                <span className={`badge ${item.status}`}>
                  {item.status === 'live' ? 'Live' : 'Draft'}
                </span>
              </span>
              {item.subtitle ? (
                <span className="grid-card-subtitle" dir="auto">
                  {item.subtitle}
                </span>
              ) : null}
              {item.facts?.length ? (
                <span className="grid-card-facts">{item.facts.join(' · ')}</span>
              ) : null}
            </span>
          </Link>

          <div className="grid-card-actions">
            <button
              type="button"
              className="btn icon"
              aria-label={`Move ${item.title} earlier`}
              disabled={index === 0}
              onClick={() => move(index, -1)}
            >
              ←
            </button>
            <button
              type="button"
              className="btn icon"
              aria-label={`Move ${item.title} later`}
              disabled={index === items.length - 1}
              onClick={() => move(index, 1)}
            >
              →
            </button>
            {canDelete && onDelete ? (
              <button
                type="button"
                className="btn icon danger"
                aria-label={`${deleteLabel} ${item.title}`}
                onClick={() => onDelete(item.id)}
              >
                ×
              </button>
            ) : null}
          </div>
        </li>
      ))}
    </ul>
  );
}

/** Status filter for the catalog views. Counts come from the same data the grid renders. */
export function StatusFilter({
  value,
  onChange,
  counts,
}: {
  value: 'all' | 'live' | 'draft';
  onChange: (next: 'all' | 'live' | 'draft') => void;
  counts: { all: number; live: number; draft: number };
}) {
  const options = [
    { key: 'all' as const, label: 'All' },
    { key: 'live' as const, label: 'Live' },
    { key: 'draft' as const, label: 'Draft' },
  ];
  return (
    <div className="filter-chips" role="group" aria-label="Filter by status">
      {options.map((option) => (
        <button
          key={option.key}
          type="button"
          className={value === option.key ? 'chip active' : 'chip'}
          aria-pressed={value === option.key}
          onClick={() => onChange(option.key)}
        >
          {option.label}
          <span className="chip-count">{counts[option.key]}</span>
        </button>
      ))}
    </div>
  );
}
